// Typed fetch helpers. Sends cookies (session) automatically via credentials: "include".

export type Me = {
  id: string;
  name: string;
  email: string;
  function: string;
  roles: string[];
  highest: string;
};

export type RosterRow = {
  id: string;
  name: string;
  email: string;
  function: string;
  manager_id?: string;
  is_pilot: boolean;
  is_manager: boolean;
  is_champion: boolean;
  is_strategist: boolean;
  is_ceo: boolean;
  start_date?: string;
  active: boolean;
};

export type Tier = "Tier 1" | "Tier 2" | "Tier 3" | "None";

export type WeeklyInput = {
  q2_tiers: Tier[];
  q3_days_used: number;
  q4_top_use_case: string;
  q5_win: boolean;
  q5a_outcome?: string;
  q5b_hours?: number;
  q5c_tier?: Tier;
  q5d_high_impact?: boolean;
  q5e_manager_id?: string;
  q6_xp_activities: string[];
  q6a_endorser?: string;
  q6b_prompt_link?: string;
  q7_blocker?: string;
};

export type WeeklySubmitted = {
  response_id: string;
  win_id?: string | null;
  xp_awarded: number;
  week_iso: number;
  week_year: number;
};

export type Win = {
  id: string;
  submitted_at: string;
  user_id: string;
  user_name: string;
  function: string;
  outcome: string;
  est_hours_saved: number;
  tier_used: string;
  high_impact: boolean;
  manager_id: string;
  manager_name: string;
  attestation_status: "pending" | "verified" | "rejected";
  verified_at?: string;
};

export type Level = {
  user_id: string;
  name: string;
  function: string;
  total_xp: number;
  level: string;
  wins_logged: number;
  wins_verified: number;
  weeks_since_last_submission?: number;
  computed_at: string;
};

export type LevelDistRow = { level: string; count: number };

export type CohortSlice = {
  active_seats_this_week: number;
  weekly_active_users: number;
  onboarding_rate: number;
  total_hours_saved_90d: number;
  high_impact_wins_90d: number;
  head_count: number;
};

export type FunctionSlice = {
  function: string;
  head_count: number;
  active_seats_this_week: number;
  weekly_active_users: number;
  hours_saved_90d: number;
  high_impact_wins_90d: number;
  practitioner_plus_rate: number;
};

export type Dashboard = {
  as_of: string;
  active_seats_this_week: number;
  weekly_active_users: number;
  onboarding_rate: number;
  level_distribution: LevelDistRow[];
  total_hours_saved_90d: number;
  high_impact_wins_90d: number;
  attestation_latency_median_days?: number;
  pilot_cohort: CohortSlice;
  active_pending_attestations: number;
  per_function: FunctionSlice[];
};

class APIError extends Error {
  constructor(public status: number, public body: string) {
    super(`API ${status}: ${body}`);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  const r = await fetch(path, init);
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new APIError(r.status, text);
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

export const api = {
  me:        () => request<Me>("GET", "/auth/me"),
  logout:    () => request<void>("POST", "/auth/logout"),
  devLogin:  (email: string) =>
    request<{ status: string; email: string }>("GET", `/auth/dev-login?email=${encodeURIComponent(email)}`),

  roster: {
    list:    () => request<RosterRow[]>("GET", "/api/admin/roster/"),
    create:  (input: Partial<RosterRow>) => request<{ id: string }>("POST", "/api/admin/roster/", input),
    update:  (id: string, input: Partial<RosterRow>) => request<void>("PATCH", `/api/admin/roster/${id}`, input),
    delete:  (id: string) => request<void>("DELETE", `/api/admin/roster/${id}`),
  },

  email: {
    test: (to: string, subject: string, body: string) =>
      request<{ status: string; dedup_key: string }>("POST", "/api/admin/email/test", { to, subject, body }),
  },

  intake: {
    submitWeekly: (input: WeeklyInput) =>
      request<WeeklySubmitted>("POST", "/api/weekly", input),
    submitBaseline: (answers: Record<string, unknown>) =>
      request<{ id: string }>("POST", "/api/baseline", answers),
  },

  wins: {
    mine: () => request<Win[]>("GET", "/api/wins/mine"),
    pendingForMe: () => request<Win[]>("GET", "/api/attestations/pending"),
    attest: (id: string, decision: "verified" | "rejected", manager_note: string) =>
      request<{ win_id: string; decision: string }>("POST", `/api/wins/${id}/attest`, { decision, manager_note }),
  },

  levels: {
    me: () => request<Level>("GET", "/api/levels/me"),
    list: (fn?: string) =>
      request<Level[]>("GET", `/api/levels${fn ? `?function=${encodeURIComponent(fn)}` : ""}`),
    recompute: () => request<{ status: string }>("POST", "/api/admin/levels/recompute"),
  },

  dashboard: {
    cohort: () => request<Dashboard>("GET", "/api/dashboard/"),
    byFunction: (fn: string) =>
      request<Dashboard>("GET", `/api/dashboard/function/${encodeURIComponent(fn)}`),
  },

  pilotCandidates: {
    list:   () => request<PilotCandidate[]>("GET", "/api/admin/pilot-candidates/"),
    create: (input: Partial<PilotCandidateInput>) =>
      request<{ id: string }>("POST", "/api/admin/pilot-candidates/", input),
    update: (id: string, input: Partial<PilotCandidateInput>) =>
      request<void>("PATCH", `/api/admin/pilot-candidates/${id}`, input),
    delete: (id: string) => request<void>("DELETE", `/api/admin/pilot-candidates/${id}`),
  },

  pilotCohort: {
    list:   () => request<PilotCohortRow[]>("GET", "/api/admin/pilot-cohort/"),
    create: (input: Partial<PilotCohortInput>) =>
      request<{ id: string }>("POST", "/api/admin/pilot-cohort/", input),
    update: (id: string, input: Partial<PilotCohortInput>) =>
      request<void>("PATCH", `/api/admin/pilot-cohort/${id}`, input),
  },

  pilotUseCases: {
    listByPilot: (pilotId: string) =>
      request<PilotUseCase[]>("GET", `/api/admin/pilot-use-cases/?pilot_id=${encodeURIComponent(pilotId)}`),
    list:   () => request<PilotUseCase[]>("GET", "/api/admin/pilot-use-cases/"),
    create: (input: Partial<PilotUseCaseInput>) =>
      request<{ id: string }>("POST", "/api/admin/pilot-use-cases/", input),
    update: (id: string, input: Partial<PilotUseCaseInput>) =>
      request<void>("PATCH", `/api/admin/pilot-use-cases/${id}`, input),
    delete: (id: string) => request<void>("DELETE", `/api/admin/pilot-use-cases/${id}`),
  },

  champions: {
    list:   () => request<ChampionRow[]>("GET", "/api/admin/champions/"),
    create: (input: Partial<ChampionInput>) =>
      request<{ id: string }>("POST", "/api/admin/champions/", input),
    update: (id: string, input: Partial<ChampionInput>) =>
      request<void>("PATCH", `/api/admin/champions/${id}`, input),
  },

  playbookEdits: {
    list:   (status?: string) =>
      request<PlaybookEdit[]>("GET", `/api/admin/playbook-edits/${status ? `?status=${status}` : ""}`),
    create: (input: Partial<PlaybookEditInput>) =>
      request<{ id: string }>("POST", "/api/admin/playbook-edits/", input),
    update: (id: string, input: Partial<PlaybookEditInput>) =>
      request<void>("PATCH", `/api/admin/playbook-edits/${id}`, input),
  },

  retros: {
    submit: (retroType: "week-6" | "week-12", pilotId: string, body: RetroInput) =>
      request<{ retro_id: string; playbook_edit_ids: string[] }>(
        "POST",
        `/api/retros/${retroType}/${pilotId}`,
        body,
      ),
    list:        () => request<RetroSummary[]>("GET", "/api/retros/"),
    byPilot:     (pilotId: string) => request<RetroDetail[]>("GET", `/api/retros/pilot/${pilotId}`),
  },

  ceoCommitments: {
    list:   (status?: string) =>
      request<CEOCommitment[]>("GET", `/api/admin/ceo-commitments/${status ? `?status=${status}` : ""}`),
    create: (input: Partial<CEOCommitmentInput>) =>
      request<{ id: string }>("POST", "/api/admin/ceo-commitments/", input),
    update: (id: string, input: Partial<CEOCommitmentInput>) =>
      request<void>("PATCH", `/api/admin/ceo-commitments/${id}`, input),
  },

  championCommitments: {
    list:   (status?: string) =>
      request<ChampionCommitment[]>("GET", `/api/admin/champion-commitments/${status ? `?status=${status}` : ""}`),
    create: (input: Partial<ChampionCommitmentInput>) =>
      request<{ id: string }>("POST", "/api/admin/champion-commitments/", input),
    update: (id: string, input: Partial<ChampionCommitmentInput>) =>
      request<void>("PATCH", `/api/admin/champion-commitments/${id}`, input),
  },

  digest: {
    list: (targetMonday?: string) =>
      request<{ target_monday: string; digests: DigestSummary[] }>(
        "GET",
        `/api/admin/digest-preview/${targetMonday ? `?target_monday=${targetMonday}` : ""}`,
      ),
    sendAll: (managerIds?: string[], targetMonday?: string) =>
      request<{ target_monday: string; enqueued: number; skipped: number }>(
        "POST",
        "/api/admin/digest-preview/send",
        { manager_ids: managerIds, target_monday: targetMonday },
      ),
  },

  ops: {
    health: () => request<OpsHealth>("GET", "/api/admin/ops/health"),
  },

  audit: {
    list: (filters?: AuditFilters) => {
      const p = new URLSearchParams();
      if (filters?.actor_id) p.set("actor_id", filters.actor_id);
      if (filters?.action_prefix) p.set("action_prefix", filters.action_prefix);
      if (filters?.target_table) p.set("target_table", filters.target_table);
      if (filters?.since) p.set("since", filters.since);
      if (filters?.until) p.set("until", filters.until);
      if (filters?.limit) p.set("limit", String(filters.limit));
      const qs = p.toString();
      return request<AuditEntry[]>("GET", `/api/admin/audit/${qs ? `?${qs}` : ""}`);
    },
  },

  preread: {
    ceoRetro: (scheduledFor: string, asks?: string[]) =>
      request<PrereadDoc>(
        "GET",
        `/api/admin/preread/ceo-retro?scheduled_for=${encodeURIComponent(scheduledFor)}${asks ? `&asks=${encodeURIComponent(asks.join("|"))}` : ""}`,
      ),
    championForum: (scheduledFor: string, theme?: string) =>
      request<PrereadDoc>(
        "GET",
        `/api/admin/preread/champion-forum?scheduled_for=${encodeURIComponent(scheduledFor)}${theme ? `&theme=${encodeURIComponent(theme)}` : ""}`,
      ),
    ninetyDay: (input: NinetyDayPreviewInput) => {
      const params = new URLSearchParams();
      if (input.cohort_label) params.set("cohort_label", input.cohort_label);
      if (input.path) params.set("path", input.path);
      if (input.label) params.set("label", input.label);
      if (input.reasoning) params.set("reasoning", input.reasoning);
      if (input.risks?.length) params.set("risks", input.risks.join("|"));
      if (input.questions?.length) params.set("questions", input.questions.join("|"));
      return request<PrereadDoc>("GET", `/api/admin/preread/90day?${params.toString()}`);
    },
  },
};

export type DigestSummary = {
  manager_id: string;
  manager_name: string;
  manager_email: string;
  subject: string;
  dedup_key: string;
};

export type PrereadDoc = {
  subject: string;
  body_html: string;
  recipients: string[];
  dedup_key: string;
};

export type NinetyDayPreviewInput = {
  cohort_label?: string;
  path?: "A" | "B";
  label?: string;
  reasoning?: string;
  risks?: string[];
  questions?: string[];
};

// ---------- Ledger types ----------

export type PilotCandidate = {
  id: string;
  candidate_id: string;
  candidate_name: string;
  candidate_email: string;
  function: string;
  seniority: string;
  sentiment: string;
  c1_reps: number; c2_visible: number; c3_honest: number; c4_tier3: number; c5_mentor: number;
  score: number;
  excluded: boolean;
  exclusion_reason: string;
  shortlist: boolean;
  notes: string;
};

export type PilotCandidateInput = {
  candidate_id: string;
  function: string;
  seniority: string;
  sentiment: string;
  c1_reps: number; c2_visible: number; c3_honest: number; c4_tier3: number; c5_mentor: number;
  excluded: boolean;
  exclusion_reason: string;
  notes: string;
};

export type PilotCohortRow = {
  id: string;
  pilot_id: string;
  pilot_name: string;
  pilot_email: string;
  function: string;
  seniority: string;
  sentiment: string;
  manager_id: string;
  manager_name: string;
  mgr_confirmed_at?: string;
  invited_at?: string;
  kickoff_at?: string;
  status: string;
  withdrawal_reason: string;
  notes: string;
};

export type PilotCohortInput = {
  pilot_id: string;
  function: string;
  seniority: string;
  sentiment: string;
  manager_id: string;
  status: string;
  mgr_confirmed_at?: string;
  invited_at?: string;
  kickoff_at?: string;
  withdrawal_reason?: string;
  notes?: string;
};

export type PilotUseCase = {
  id: string;
  pilot_id: string;
  pilot_name: string;
  description: string;
  tier: string;
  target_hours_saved?: number;
  target_first_win_date?: string;
  status: string;
  evolved_to_description: string;
  notes: string;
};

export type PilotUseCaseInput = {
  pilot_id: string;
  description: string;
  tier: string;
  target_hours_saved?: number;
  target_first_win_date?: string;
  status: string;
  evolved_to_description?: string;
  notes?: string;
};

export type ChampionRow = {
  id: string;
  function: string;
  champion_id?: string;
  champion_name: string;
  term_start?: string;
  term_end?: string;
  status: string;
  handoff_notes: string;
  notes: string;
};

export type ChampionInput = {
  function: string;
  champion_id?: string;
  term_start?: string;
  term_end?: string;
  status?: string;
  handoff_notes?: string;
  notes?: string;
};

export type PlaybookEdit = {
  id: string;
  source_pilot_id?: string;
  source_pilot_name: string;
  source: string;
  target_file: string;
  section: string;
  current_text: string;
  proposed_change: string;
  rationale: string;
  status: string;
  decided_by?: string;
  decided_by_name: string;
  decided_at?: string;
  decision_notes: string;
  created_at: string;
};

export type PlaybookEditInput = {
  target_file: string;
  section?: string;
  current_text?: string;
  proposed_change: string;
  rationale?: string;
  status?: string;
  decision_notes?: string;
};

export type RetroInput = {
  conducted_at?: string;
  use_case_status?: unknown;
  what_worked: string;
  what_failed: string;
  what_revealed?: string;
  second_half_plan?: string;
  handoff_bullets?: string;
  champion_track_flag?: "Yes" | "Maybe" | "No";
  nps_score?: number;
  final_use_case_stats?: unknown;
  playbook_edits: Array<{
    target_file: string;
    section: string;
    current_text: string;
    proposed_change: string;
    rationale: string;
  }>;
};

export type RetroSummary = {
  id: string;
  pilot_id: string;
  pilot_name: string;
  retro_type: string;
  conducted_at: string;
  edit_count: number;
  champion_track_flag?: string;
  nps_score?: number;
};

export type RetroDetail = {
  id: string;
  retro_type: string;
  conducted_at: string;
  what_worked: string;
  what_failed: string;
  what_revealed?: string;
  second_half_plan?: string;
  handoff_bullets?: string;
  edit_count: number;
  champion_track_flag?: string;
  nps_score?: number;
};

export type CEOCommitment = {
  id: string;
  retro_date: string;
  commitment: string;
  owner_id?: string;
  owner_name: string;
  due_at?: string;
  source_pilots: string[];
  status: string;
  drop_reason: string;
  notes: string;
};

export type CEOCommitmentInput = {
  retro_date: string;
  commitment: string;
  owner_id?: string;
  due_at?: string;
  source_pilots?: string[];
  status?: string;
  drop_reason?: string;
  notes?: string;
};

export type ChampionCommitment = {
  id: string;
  forum_date: string;
  champion_id: string;
  champion_name: string;
  commitment: string;
  beneficiary_functions: string[];
  due_at?: string;
  status: string;
  drop_reason: string;
  notes: string;
};

export type ChampionCommitmentInput = {
  forum_date: string;
  champion_id: string;
  commitment: string;
  beneficiary_functions: string[];
  due_at?: string;
  status?: string;
  drop_reason?: string;
  notes?: string;
};

// ---------- Ops + audit ----------

export type JobHealthRow = {
  name: string;
  last_run?: string;
  last_status: string;
  last_error?: string;
  hours_since_run?: number;
  expected_within: string;
  stale: boolean;
};

export type OpsHealth = {
  as_of: string;
  overall_status: "ok" | "degraded" | "failing";
  issues: string[];
  jobs: JobHealthRow[];
  outbox: {
    pending: number;
    retry_exhausted: number;
    sent_last_24h: number;
    last_successful_send?: string;
    oldest_pending?: string;
  };
  sessions: { active: number; oldest_seen?: string };
  database: { size_bytes: number; size_pretty: string };
};

export type AuditFilters = {
  actor_id?: string;
  action_prefix?: string;
  target_table?: string;
  since?: string;
  until?: string;
  limit?: number;
};

export type AuditEntry = {
  id: number;
  at: string;
  actor_name: string;
  actor_email?: string;
  action: string;
  target_table: string;
  target_id: string;
  before?: unknown;
  after?: unknown;
  notes?: string;
};

export { APIError };
